import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import {
  BRAZILIAN_STATE_INFO,
  format as formatMoney,
  subtract,
  type BrazilianState,
  type Money,
} from '@evogard/domain';
import type { FinanceDashboardRowWire } from '@evogard/contract';
import {
  BRAZIL_MAP_SIZE,
  BRAZIL_MAP_STATES,
} from '../../../components/map/brazilStates.js';
import { compact, money } from './money.js';

/**
 * O país inteiro numa olhada, e cada estado por dentro (29/10/2026): a
 * receita e a despesa pelo endereço do associado, no mesmo mapa do Painel da
 * Gestão do sinistro (`components/map/brazilStates.ts`, da malha do IBGE).
 *
 * O desenho é o de lá — coroplético em cinco faixas, a área sem lançamento
 * sem cor, a lista ordenada ao lado com o valor exato, a dica ao passar o
 * mouse e "Ver tabela". O que muda é o que pinta:
 *
 * - **Receitas**: azul, do claro ao cheio;
 * - **Despesas**: laranja, do claro ao cheio;
 * - **Saldo**: as duas cores — azul onde a receita passa a despesa, laranja
 *   onde a despesa passa a receita —, e a faixa pela distância do zero.
 *
 * ## Clicar num estado abre o estado
 *
 * O mapa troca o país pelos municípios dele (`components/map/cities/<IBGE>.ts`,
 * gerados por `tools/geo/state-cities.mjs`), carregados só nessa hora, e a
 * lista ao lado vira a das cidades. A cidade do cadastro casa com o
 * município pelo nome sem acento e sem caixa — o SGA grava `FORTALEZA`, o
 * IBGE `Fortaleza`. A que não casa com nenhum (nome escrito de outro jeito no
 * cadastro) é dita embaixo do mapa, com o valor, e não some.
 *
 * ## A transição
 *
 * Abrir um estado é um zoom: o `viewBox` do país anda até o contorno do
 * estado enquanto os outros apagam, e só então os municípios entram, num
 * leve aumento com o esmaecido revertendo. Voltar faz o caminho inverso — o
 * país nasce enquadrado no estado e se afasta. Os municípios começam a baixar
 * no clique, para estarem prontos quando o zoom termina. Quem pediu ao
 * sistema menos movimento (`prefers-reduced-motion`) recebe a troca direta.
 *
 * ## Aproximar e mover
 *
 * A roda do mouse aproxima e afasta em volta do ponteiro, arrastar move, e o
 * clique duplo aproxima. Os botões + / − / Enquadrar fazem o mesmo para quem
 * não usa a roda. A linha e o nome não engordam com o zoom: a linha usa
 * `non-scaling-stroke`, e o tamanho do texto acompanha o quadro. Aproximado o
 * bastante, todo município com lançamento ganha o nome, e não só os cinco
 * maiores. Arrastar não conta como clique: soltar sobre um estado depois de
 * mover o mapa não abre o estado.
 */

type Metric = 'saldo' | 'receitas' | 'despesas';

// @valor
const METRIC_LABELS: Readonly<Record<Metric, string>> = {
  saldo: 'Saldo',
  receitas: 'Receitas',
  despesas: 'Despesas',
};

const STEPS = 5;
/* A opacidade em cada faixa — a mesma do mapa do sinistro. */
const STEP_OPACITY = [0.22, 0.4, 0.58, 0.78, 1] as const;

/** A faixa de 0 a 4 pela distância do zero, ou `null` para o zero. */
function stepOf(value: number, max: number): number | null {
  const v = Math.abs(value);
  if (v === 0) return null;
  return Math.min(STEPS - 1, Math.floor((v / max) * STEPS));
}

/* As classes escritas por inteiro: o Tailwind só gera a classe que lê no código. */
const FILL = { azul: 'fill-fase-azul', laranja: 'fill-fase-laranja' } as const;
const SWATCH = { azul: 'bg-fase-azul', laranja: 'bg-fase-laranja' } as const;
type Hue = keyof typeof FILL;

/** Uma área do mapa — estado ou município — e o que caiu nela. */
type Area = {
  key: string;
  name: string;
  row: FinanceDashboardRowWire;
  revenue: Money;
  expense: Money;
  balance: Money;
};

/** O desenho de uma área: o contorno e o ponto do rótulo. */
type Shape = { key: string; label: string; d: string; x: number; y: number };

const isState = (key: string): key is BrazilianState => key in BRAZILIAN_STATE_INFO;

/** "SÃO JOÃO DO JAGUARIBE" e "São João do Jaguaribe" viram a mesma chave. */
export const placeKey = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim();

const areaOf = (key: string, name: string, row: FinanceDashboardRowWire): Area => {
  const revenue = money(row.revenue.amount);
  const expense = money(row.expense.amount);
  return { key, name, row, revenue, expense, balance: subtract(revenue, expense) };
};

/* Os municípios de cada estado, um arquivo por UF, baixado quando o estado é aberto. */
const CITY_FILES = import.meta.glob<{
  size: { width: number; height: number };
  outline: string;
  cities: readonly { id: string; name: string; d: string; x: number; y: number }[];
}>('../../../components/map/cities/*.ts');

const cityShapesQuery = (uf: string | null) => ({
  queryKey: ['mapa', 'municipios', uf],
  staleTime: Infinity,
  queryFn: async () => {
    const code = uf && isState(uf) ? BRAZILIAN_STATE_INFO[uf].ibge : uf;
    const load = CITY_FILES[`../../../components/map/cities/${code}.ts`];
    if (!load) throw new Error(`Não há o desenho dos municípios de ${uf}.`);
    return load();
  },
});

function useCityShapes(uf: string | null) {
  return useQuery({ ...cityShapesQuery(uf), enabled: uf !== null });
}

const reducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** O zoom em andamento: para dentro de um estado, ou de volta ao país saindo dele. */
type Zoom = { key: string; direction: 'in' | 'out' };

export function FinanceStatesMap({
  states: stateRows,
  cities: cityRows,
  loading,
  selected,
  onSelect,
}: {
  states: readonly FinanceDashboardRowWire[] | undefined;
  cities: readonly FinanceDashboardRowWire[] | undefined;
  loading: boolean;
  selected: string | null;
  onSelect: (state: string | null) => void;
}) {
  const [metric, setMetric] = useState<Metric>('saldo');
  const [asTable, setAsTable] = useState(false);
  const [zoom, setZoom] = useState<Zoom | null>(null);
  const shapes = useCityShapes(selected);
  const queryClient = useQueryClient();

  /* Abrir: baixa os municípios já no clique e faz o zoom; o estado só é escolhido no fim dele. */
  const openState = (key: string) => {
    if (reducedMotion()) return onSelect(key);
    void queryClient.prefetchQuery(cityShapesQuery(key));
    setZoom({ key, direction: 'in' });
  };
  /* Voltar: o país nasce enquadrado no estado e se afasta. */
  const backToCountry = () => {
    if (selected && !reducedMotion()) setZoom({ key: selected, direction: 'out' });
    onSelect(null);
  };

  const list = stateRows ?? [];
  const states: Area[] = list.flatMap((row) =>
    row.gap || !isState(row.key)
      ? []
      : [areaOf(row.key, BRAZILIAN_STATE_INFO[row.key].name, row)],
  );
  const outsideCountry = list.filter((r) => r.gap || !isState(r.key));

  /* Aberto um estado: as cidades dele, casadas com os municípios do IBGE pelo nome. */
  const cityShapes = shapes.data?.cities ?? [];
  const idByName = new Map(cityShapes.map((c) => [placeKey(c.name), c]));
  const inState = (cityRows ?? []).filter(
    (r) => !r.gap && selected && r.key.endsWith(`/${selected}`),
  );
  const cities: Area[] = [];
  const unmatched: FinanceDashboardRowWire[] = [];
  for (const row of inState) {
    const shape = idByName.get(placeKey(row.key.slice(0, row.key.lastIndexOf('/'))));
    if (shape) cities.push(areaOf(shape.id, shape.name, row));
    else unmatched.push(row);
  }

  const open = selected !== null;
  const areas = open ? cities : states;
  const valueOf = (a: Area) =>
    metric === 'saldo' ? a.balance : metric === 'receitas' ? a.revenue : a.expense;
  const figureShapes: Shape[] = open
    ? cityShapes.map((c) => ({ key: c.id, label: c.name, d: c.d, x: c.x, y: c.y }))
    : BRAZIL_MAP_STATES.map((s) => ({ key: s.uf, label: s.uf, d: s.d, x: s.x, y: s.y }));
  const size = open ? (shapes.data?.size ?? BRAZIL_MAP_SIZE) : BRAZIL_MAP_SIZE;
  const stateName =
    selected && isState(selected) ? BRAZILIAN_STATE_INFO[selected].name : selected;
  const outside = open ? unmatched : outsideCountry;

  return (
    <Card
      as="figure"
      className="@container"
      title={open ? `${stateName}, por cidade` : 'Pelo país'}
      descricao={
        open
          ? 'Pelo endereço do associado no cadastro.'
          : 'Pelo endereço do associado no cadastro. Clique num estado para ver as cidades dele.'
      }
      actions={
        list.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {open && (
              <Button variant="contorno" size="sm" onClick={backToCountry}>
                <Icon nome="seta-esquerda" size={16} />
                Voltar ao país
              </Button>
            )}
            {!asTable &&
              (['saldo', 'receitas', 'despesas'] as const).map((m) => (
                <Button
                  key={m}
                  variant={metric === m ? 'secundario' : 'discreto'}
                  size="sm"
                  aria-pressed={metric === m}
                  onClick={() => setMetric(m)}
                >
                  {METRIC_LABELS[m]}
                </Button>
              ))}
            <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
              <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
              {asTable ? 'Ver mapa' : 'Ver tabela'}
            </Button>
          </div>
        )
      }
    >
      {(loading && list.length === 0) || (open && shapes.isLoading) ? (
        <div
          className="h-80 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : list.length === 0 ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum lançamento no período. Quando houver, o mapa mostra de que estado é cada
          associado.
        </p>
      ) : open && shapes.isError ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          {shapes.error.message}
        </p>
      ) : asTable ? (
        <AreasTable areas={areas} first={open ? 'Cidade' : 'Estado'} />
      ) : (
        <div className="grid items-start gap-5 @lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
          <AreasFigure
            key={selected ?? 'BR'}
            zoom={open ? null : zoom}
            onZoomed={() => {
              const done = zoom;
              setZoom(null);
              if (done?.direction === 'in') onSelect(done.key);
            }}
            shapes={figureShapes}
            outline={open ? shapes.data?.outline : undefined}
            size={size}
            areas={areas}
            metric={metric}
            valueOf={valueOf}
            dense={open}
            onPick={open || zoom ? undefined : openState}
            label={open ? `Mapa de ${stateName} por cidade` : 'Mapa do Brasil por estado'}
          />
          {areas.length === 0 ? (
            <p className="text-sm text-texto-suave">
              {open
                ? `Nenhum lançamento de associado com cidade em ${stateName} no período.`
                : 'Nenhum lançamento com endereço no período.'}
            </p>
          ) : (
            <Ranking
              areas={areas}
              metric={metric}
              valueOf={valueOf}
              limit={open ? 12 : undefined}
              onPick={open || zoom ? undefined : openState}
            />
          )}
        </div>
      )}
      {outside.length > 0 && (
        <figcaption className="mt-3 text-xs text-texto-suave">
          {open
            ? 'Cidades do cadastro que não casam com um município do IBGE: '
            : 'Fora do mapa: '}
          {outside
            .map(
              (r) =>
                `${r.label} (${formatMoney(subtract(money(r.revenue.amount), money(r.expense.amount)))})`,
            )
            .join(' · ')}
          .
        </figcaption>
      )}
    </Card>
  );
}

function AreasFigure({
  shapes,
  size,
  outline,
  areas,
  metric,
  valueOf,
  dense,
  onPick,
  label,
  zoom,
  onZoomed,
}: {
  shapes: Shape[];
  size: { width: number; height: number };
  /** O contorno do estado aberto, por cima dos municípios. */
  outline?: string;
  areas: Area[];
  metric: Metric;
  valueOf: (a: Area) => Money;
  /** Municípios: contorno fino e rótulo só nas cinco maiores, senão o nome cobre o mapa. */
  dense: boolean;
  onPick?: (key: string) => void;
  label: string;
  zoom?: Zoom | null;
  onZoomed?: () => void;
}) {
  const [tip, setTip] = useState<{ x: number; y: number; lines: string[] } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRefs = useRef(new Map<string, SVGPathElement>());
  /* Os outros estados apagados: na volta começa apagado e acende; na ida começa aceso e apaga. */
  const [dim, setDim] = useState(zoom?.direction === 'out');
  const zoomKey = zoom ? `${zoom.direction}:${zoom.key}` : null;

  useLayoutEffect(() => {
    if (!zoom) return;
    const svg = svgRef.current;
    const path = pathRefs.current.get(zoom.key);
    if (!svg || !path) return onZoomed?.();
    setTip(null);
    /* O quadro do estado, com folga, na proporção do desenho — senão o zoom deforma. */
    const b = path.getBBox();
    const aspect = size.width / size.height;
    let w = b.width * 1.25;
    let h = b.height * 1.25;
    if (w / h > aspect) h = w / aspect;
    else w = h * aspect;
    const target = [b.x + b.width / 2 - w / 2, b.y + b.height / 2 - h / 2, w, h];
    const full = [0, 0, size.width, size.height];
    /* a ida parte do quadro em que a pessoa está — ela pode ter aproximado antes de clicar */
    const [from, to] =
      zoom.direction === 'in' ? [viewRef.current, target] : [target, full];
    svg.setAttribute('viewBox', from.join(' '));
    const fade = requestAnimationFrame(() => setDim(zoom.direction === 'in'));
    const duration = 600;
    let start: number | undefined;
    let frame = 0;
    const step = (now: number) => {
      start ??= now;
      const k = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - k, 3);
      svg.setAttribute('viewBox', from.map((v, i) => v + (to[i]! - v) * eased).join(' '));
      if (k < 1) frame = requestAnimationFrame(step);
      else onZoomed?.();
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(fade);
    };
    // o zoom dispara pela troca de alvo, e não a cada render de quem o pediu
  }, [zoomKey]);

  /* ---- aproximar e mover -------------------------------------------- */
  const full = [0, 0, size.width, size.height];
  const [view, setView] = useState<number[]>(full);
  const viewRef = useRef(view);
  viewRef.current = view;
  const drag = useRef<{ x: number; y: number; view: number[]; moved: boolean } | null>(
    null,
  );
  const dragged = useRef(false);
  const maxZoom = dense ? 16 : 6;
  const scale = view[2]! / size.width;

  const clamp = ([x, y, w]: number[]) => {
    const nw = Math.min(size.width, Math.max(size.width / maxZoom, w!));
    const nh = (nw * size.height) / size.width;
    return [
      Math.min(Math.max(x!, 0), size.width - nw),
      Math.min(Math.max(y!, 0), size.height - nh),
      nw,
      nh,
    ];
  };
  /** O ponto da tela no sistema do desenho — o `viewBox` pode estar com folga dos lados. */
  const toMap = (clientX: number, clientY: number) => {
    const svg = svgRef.current!;
    const m = svg.getScreenCTM()!.inverse();
    return {
      x: m.a * clientX + m.c * clientY + m.e,
      y: m.b * clientX + m.d * clientY + m.f,
    };
  };
  const zoomAt = (factor: number, cx?: number, cy?: number) => {
    const [x, y, w, h] = viewRef.current as [number, number, number, number];
    const px = cx ?? x + w / 2;
    const py = cy ?? y + h / 2;
    const nw = w * factor;
    const k = nw / w;
    setView(clamp([px - (px - x) * k, py - (py - y) * k, nw]));
  };

  /* A roda: o ouvinte do React é passivo e não impede a página de rolar junto. */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (e: WheelEvent) => {
      if (zoom) return;
      e.preventDefault();
      setTip(null);
      const p = toMap(e.clientX, e.clientY);
      zoomAt(Math.exp(e.deltaY * 0.0015), p.x, p.y);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  });

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    if (e.button !== 0 || zoom) return;
    dragged.current = false;
    drag.current = { x: e.clientX, y: e.clientY, view: viewRef.current, moved: false };
  };
  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.hypot(dx, dy) < 4) return;
    if (!d.moved) {
      d.moved = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      setTip(null);
    }
    const unit = 1 / svgRef.current!.getScreenCTM()!.a;
    setView(clamp([d.view[0]! - dx * unit, d.view[1]! - dy * unit, d.view[2]!]));
  };
  const onPointerUp = () => {
    dragged.current = drag.current?.moved ?? false;
    drag.current = null;
  };

  const byKey = new Map(areas.map((a) => [a.key, a]));
  const max = Math.max(1, ...areas.map((a) => Math.abs(valueOf(a))));
  const labeled = new Set(
    dense && scale > 0.35
      ? [...areas]
          .sort((a, b) => Math.abs(valueOf(b)) - Math.abs(valueOf(a)))
          .slice(0, 5)
          .map((a) => a.key)
      : areas.map((a) => a.key),
  );
  const hueOf = (a: Area): Hue =>
    metric === 'receitas'
      ? 'azul'
      : metric === 'despesas'
        ? 'laranja'
        : a.balance >= 0
          ? 'azul'
          : 'laranja';
  const show = (e: PointerEvent, shape: Shape) => {
    if (drag.current?.moved) return;
    const box = (e.currentTarget as Element)
      .closest('[data-areas]')!
      .getBoundingClientRect();
    const a = byKey.get(shape.key);
    setTip({
      x: e.clientX - box.left,
      y: e.clientY - box.top,
      lines: [
        dense ? shape.label : `${a?.name ?? shape.label} (${shape.key})`,
        ...(a
          ? [
              `Receitas: ${formatMoney(a.revenue)}`,
              `Despesas: ${formatMoney(a.expense)}`,
              `Saldo: ${formatMoney(a.balance)}`,
            ]
          : ['nenhum lançamento no período']),
      ],
    });
  };

  return (
    <div
      className={cn(
        'relative',
        dense &&
          'animate-in fade-in-0 zoom-in-90 duration-500 ease-out motion-reduce:animate-none',
      )}
      data-areas
      onPointerLeave={() => setTip(null)}
    >
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={view.join(' ')}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={(e) => {
            const p = toMap(e.clientX, e.clientY);
            zoomAt(0.5, p.x, p.y);
          }}
          className={cn(
            'mx-auto block max-h-[26rem] w-full max-w-[24rem] select-none',
            scale < 1 ? 'cursor-grab touch-none active:cursor-grabbing' : 'touch-pan-y',
          )}
          role="img"
          aria-label={`${label}, ${METRIC_LABELS[metric].toLowerCase()}: ${areas
            .map((a) => `${a.name}, ${formatMoney(valueOf(a))}`)
            .join('; ')}.`}
        >
          {shapes.map((shape) => {
            const a = byKey.get(shape.key);
            const step = a ? stepOf(valueOf(a), max) : null;
            return (
              <path
                key={shape.key}
                ref={(el) => {
                  if (el) pathRefs.current.set(shape.key, el);
                  else pathRefs.current.delete(shape.key);
                }}
                d={shape.d}
                fillRule="evenodd"
                vectorEffect="non-scaling-stroke"
                strokeWidth={dense ? 0.6 : 1}
                strokeLinejoin="round"
                className={cn(
                  /*
                   * As linhas em `borda-forte`, a 3:1 do cartão nos dois temas
                   * (WCAG 1.4.11, medido em `theme.test.ts`): `borda` mede 1,15:1
                   * e sumia sobre o preenchimento. A divisa entre estados vai a
                   * 1; a linha entre municípios, a 0,5, abaixo do contorno do
                   * estado — o peso faz a hierarquia, e não a cor.
                   */
                  !a || step === null
                    ? 'fill-superficie-suave stroke-borda-forte'
                    : cn(
                        FILL[hueOf(a)],
                        dense ? 'stroke-borda-forte' : 'stroke-superficie',
                        'hover:opacity-80',
                        onPick && 'cursor-pointer',
                      ),
                  zoom && 'transition-opacity duration-500',
                  zoom && dim && shape.key !== zoom.key && 'opacity-10',
                )}
                style={
                  a && step !== null ? { fillOpacity: STEP_OPACITY[step] } : undefined
                }
                onPointerMove={(e) => show(e, shape)}
                onClick={() => a && !dragged.current && onPick?.(shape.key)}
              />
            );
          })}
          {outline && (
            <path
              d={outline}
              fill="none"
              vectorEffect="non-scaling-stroke"
              strokeWidth={1.75}
              strokeLinejoin="round"
              className="pointer-events-none stroke-borda-forte"
            />
          )}
          {/* O rótulo só onde há lançamento: na área vazia, ele seria ruído. */}
          {shapes.map((shape) => {
            const a = byKey.get(shape.key);
            if (!a || !labeled.has(shape.key)) return null;
            const strong = (stepOf(valueOf(a), max) ?? 0) >= 3;
            /*
             * O município é menor que o nome dele: o rótulo transborda para o
             * vizinho, e por isso leva um halo da cor do fundo (`paint-order`
             * desenha o contorno antes do texto) e a tinta normal — legível
             * sobre qualquer faixa, em claro e em escuro.
             */
            return (
              <text
                key={shape.key}
                x={shape.x}
                y={shape.y + 4 * scale}
                textAnchor="middle"
                fontSize={(dense ? 15 : 13) * scale}
                paintOrder={dense ? 'stroke' : undefined}
                strokeWidth={dense ? 4 * scale : undefined}
                strokeLinejoin="round"
                className={cn(
                  'pointer-events-none font-semibold',
                  zoom && 'transition-opacity duration-300',
                  zoom && dim && 'opacity-0',
                  dense ? 'fill-texto stroke-superficie' : '',
                  !dense && (strong ? 'fill-contraste' : 'fill-texto'),
                )}
              >
                {shape.label}
              </text>
            );
          })}
        </svg>
        {!zoom && (
          <div
            className="absolute right-0 top-0 flex flex-col gap-1"
            role="group"
            aria-label="Zoom do mapa"
          >
            <Button
              variant="secundario"
              size="sm"
              className="w-[1.875rem] px-0"
              onClick={() => zoomAt(0.6)}
            >
              <Icon nome="mais" size={16} />
              <span className="sr-only">Aproximar</span>
            </Button>
            <Button
              variant="secundario"
              size="sm"
              className="w-[1.875rem] px-0"
              disabled={scale >= 1}
              onClick={() => zoomAt(1 / 0.6)}
            >
              <Icon nome="menos" size={16} />
              <span className="sr-only">Afastar</span>
            </Button>
            <Button
              variant="secundario"
              size="sm"
              className="w-[1.875rem] px-0"
              disabled={scale >= 1}
              onClick={() => setView(full)}
            >
              <Icon nome="expandir" size={16} />
              <span className="sr-only">Enquadrar o mapa inteiro</span>
            </Button>
          </div>
        )}
      </div>
      <Legend metric={metric} max={max} />
      {tip && (
        <div
          role="presentation"
          className="pointer-events-none absolute z-10 rounded-borda border border-borda bg-superficie px-3 py-2 text-xs shadow-flutuante"
          style={{ left: tip.x + 12, top: tip.y + 12 }}
        >
          {tip.lines.map((line, i) => (
            <p
              key={i}
              className={i === 0 ? 'font-semibold text-texto' : 'text-texto-suave'}
            >
              {line}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/** As cinco faixas, com o valor do topo; no saldo, as duas cores de cada lado do zero. */
function Legend({ metric, max }: { metric: Metric; max: number }) {
  const swatches = (hue: Hue) =>
    STEP_OPACITY.map((opacity, i) => (
      <i
        key={`${hue}-${i}`}
        aria-hidden
        className={cn('h-3 w-4', SWATCH[hue])}
        style={{ opacity }}
      />
    ));
  const top = compact(max as Money);
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-texto-suave">
      <span className="flex items-center gap-1.5">
        <i
          aria-hidden
          className="size-3 rounded-[3px] border border-borda bg-superficie-suave"
        />
        nenhum
      </span>
      {metric === 'saldo' ? (
        <span className="flex items-center gap-1.5">
          despesa maior
          <span className="flex overflow-hidden rounded-[3px]">
            {swatches('laranja').reverse()}
            {swatches('azul')}
          </span>
          receita maior
        </span>
      ) : (
        <span className="flex items-center gap-1.5">
          <span className="flex overflow-hidden rounded-[3px]">
            {swatches(metric === 'receitas' ? 'azul' : 'laranja')}
          </span>
          até {top}
        </span>
      )}
    </div>
  );
}

/** A ordem e o valor exato: do que mais tem ao que menos tem; no saldo, do mais positivo ao mais negativo. */
function Ranking({
  areas,
  metric,
  valueOf,
  limit,
  onPick,
}: {
  areas: Area[];
  metric: Metric;
  valueOf: (a: Area) => Money;
  limit?: number;
  onPick?: (key: string) => void;
}) {
  const [all, setAll] = useState(false);
  const sorted = [...areas].sort((a, b) => valueOf(b) - valueOf(a));
  const shown = limit && !all ? sorted.slice(0, limit) : sorted;
  const max = Math.max(1, ...areas.map((a) => Math.abs(valueOf(a))));
  return (
    <div className="flex flex-col gap-2">
      <ol
        className="flex flex-col gap-0.5"
        aria-label={`Por ${METRIC_LABELS[metric].toLowerCase()}`}
      >
        {shown.map((a) => {
          const v = valueOf(a);
          const hue: Hue =
            metric === 'receitas'
              ? 'azul'
              : metric === 'despesas'
                ? 'laranja'
                : v >= 0
                  ? 'azul'
                  : 'laranja';
          const content = (
            <>
              <span className="truncate text-texto">{a.name}</span>
              <span className="h-2 overflow-hidden rounded-pilula bg-superficie-suave">
                <span
                  className={cn('block h-full rounded-pilula', SWATCH[hue])}
                  style={{ width: `${(Math.abs(v) / max) * 100}%` }}
                />
              </span>
              <span
                className={cn(
                  'text-right text-xs font-semibold tabular-nums',
                  v < 0 ? 'text-erro' : 'text-texto',
                )}
              >
                {metric === 'saldo' && v > 0 ? '+' : ''}
                {compact(v)}
              </span>
            </>
          );
          const grid =
            'grid w-full grid-cols-[minmax(0,9rem)_minmax(0,1fr)_auto] items-center gap-3 rounded-borda px-1.5 py-1 text-left text-sm';
          return (
            <li key={a.key}>
              {onPick ? (
                <button
                  type="button"
                  onClick={() => onPick(a.key)}
                  className={cn(grid, 'hover:bg-superficie-suave')}
                >
                  {content}
                </button>
              ) : (
                <div className={grid}>{content}</div>
              )}
            </li>
          );
        })}
      </ol>
      {limit && sorted.length > limit && (
        <div>
          <Button variant="discreto" size="sm" onClick={() => setAll((v) => !v)}>
            {all ? `Ver só as ${limit} primeiras` : `Ver todas (${sorted.length})`}
          </Button>
        </div>
      )}
    </div>
  );
}

function AreasTable({ areas, first }: { areas: Area[]; first: string }) {
  const columns: Column<Area>[] = [
    { title: first, cell: (a) => a.name, alignment: 'inicio' },
    { title: 'Receitas', cell: (a) => formatMoney(a.revenue), alignment: 'fim' },
    {
      title: 'Recebido',
      cell: (a) => formatMoney(money(a.row.revenue.settled)),
      alignment: 'fim',
    },
    { title: 'Despesas', cell: (a) => formatMoney(a.expense), alignment: 'fim' },
    {
      title: 'Pago',
      cell: (a) => formatMoney(money(a.row.expense.settled)),
      alignment: 'fim',
    },
    { title: 'Saldo', cell: (a) => formatMoney(a.balance), alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={[...areas].sort((a, b) => b.balance - a.balance)}
      rowKey={(a) => a.key}
      label={`Receita e despesa por ${first.toLowerCase()}`}
      empty="Nenhum lançamento com endereço no período."
    />
  );
}
